import { createTheme } from '@mui/material/styles'

// Cor única para texto secundário/desactivado (legendas, placeholders,
// campos só-leitura). Substitui os defaults da MUI — `text.disabled`
// (rgba(0,0,0,0.38), ~2.7:1) e `action.disabled` (rgba(0,0,0,0.26), ~1.9:1)
// — que falham o mínimo AA (4.5:1). Este tom dá >6:1 sobre branco e sobre
// os cinzas claros já usados em campos/botões desactivados.
export const MUTED_TEXT_COLOR = '#4B5563'
export const DISABLED_BACKGROUND = '#E5E7EB'

const theme = createTheme({
  palette: {
    primary: {
      main: '#023160',
      light: '#3A6291',
      dark: '#011E3B',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00A9B3',
      light: '#4FC7CE',
      dark: '#007E86',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F5F5F7',
      paper: '#ffffff',
    },
    text: {
      primary: '#1A1A2E',
      secondary: '#64748B',
      disabled: MUTED_TEXT_COLOR,
    },
    action: {
      disabled: MUTED_TEXT_COLOR,
      disabledBackground: DISABLED_BACKGROUND,
    },
    error: {
      main: '#DC2626',
    },
    success: {
      main: '#16A34A',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    caption: {
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    // Cabeçalho uniforme para todas as tabelas do sistema: contraste suave,
    // texto escuro e peso suficiente para o distinguir do conteúdo.
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#EEEEEE',
          '& .MuiTableCell-root': {
            backgroundColor: '#EEEEEE',
            color: '#111827',
            fontWeight: 700,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F5F7',
          // Remove o flash cinza nativo do toque em iOS/Android — o feedback
          // táctil passa a vir só do `:active` controlado no MuiButtonBase.
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },
    // Base partilhada por Button, IconButton, ListItemButton, CardActionArea,
    // ToggleButton, etc. — dá a todos um único ponto de feedback táctil
    // (leve escurecimento ao toque/clique), em vez de cada componente definir
    // o seu próprio estado de "pressionado". Usa `brightness` em vez de
    // `opacity`: opacity esbate elementos claros/coloridos em direcção ao
    // fundo da página (nalguns casos até os torna mais claros), enquanto
    // brightness escurece sempre, de forma consistente, seja qual for o fundo.
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:active': {
            filter: 'brightness(0.92)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 10,
          padding: '10px 20px',
          fontSize: '0.9375rem',
        },
        contained: {
          boxShadow: '0 1px 3px rgba(2,49,96,0.25)',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(2,49,96,0.35)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#fff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#023160',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#023160',
              borderWidth: '1.5px',
            },
            // Aspecto de "só leitura" para campos desactivados (ex: registos
            // que já não são rascunho) — o fundo branco/borda normal por
            // omissão fazia-os parecer editáveis apesar do `disabled`.
            '&.Mui-disabled': {
              backgroundColor: '#F5F5F5',
              cursor: 'not-allowed',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E0E0E0',
              },
            },
          },
          '& .MuiOutlinedInput-input.Mui-disabled, & .MuiSelect-select.Mui-disabled': {
            color: MUTED_TEXT_COLOR,
            WebkitTextFillColor: MUTED_TEXT_COLOR,
            cursor: 'not-allowed',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#023160',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          // O placeholder nativo do browser usa opacidade variável (e falha
          // AA em Chrome/Edge, ~3:1) — fixa-se numa cor sólida consistente.
          '&::placeholder': {
            color: MUTED_TEXT_COLOR,
            opacity: 1,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // borderRadius: 14,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-active': {
            color: '#023160',
          },
          '&.Mui-completed': {
            color: '#3A6291',
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          '&.Mui-active': {
            fontWeight: 500,
            color: '#023160',
          },
        },
      },
    },
  },
})

export default theme
